// components/dashboard/ParcelasCards.tsx
import React, { useState, useEffect } from 'react';
import { dashboardService } from '../../services/dashboardService';
import { useAuth } from '../../hooks/useAuth';

interface ParcelasCardsProps {
  year?: string;
  month?: string;
}

export const ParcelasCards: React.FC<ParcelasCardsProps> = ({ year, month }) => {
  const { user, isGrupo, isEmpresa } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setLoading(true);
      setError(null);

      try {
        const params: any = {};
        if (year) params.year = parseInt(year);
        if (month) params.month = parseInt(month);

        let response;
        if (isGrupo && user.grupoId) {
          response = await dashboardService.grupoParcelasOverview(user.grupoId, params);
        } else if (isEmpresa && user.empresaId) {
          response = await dashboardService.parcelasOverview(user.empresaId, params);
        } else {
          throw new Error('Usuário não tem empresa ou grupo associado');
        }

        setData(response.data || response);
      } catch (err: any) {
        console.error('Erro ao carregar dados de parcelas:', err);
        setError(err.message || 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, year, month, isGrupo, isEmpresa]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-gray-200 h-20 rounded-lg"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
        <p className="text-red-600 text-xs">{error}</p>
      </div>
    );
  }

  const cards = [
    {
      title: 'Total de Parcelas',
      value: data?.totalParcelas || 0,
      subtitle: 'parcelas cadastradas',
      color: '#7A61B2',
      bgColor: '#F0EDFF'
    },
    {
      title: 'Parcelas Pagas',
      value: data?.parcelasPagas || 0,
      subtitle: 'parcelas quitadas',
      color: '#2eb897',
      bgColor: '#E8F8F4'
    },
    {
      title: 'Parcelas Pendentes',
      value: data?.parcelasPendentes || 0,
      subtitle: 'aguardando pagamento',
      color: '#F9A826',
      bgColor: '#FEF3E2'
    },
    {
      title: 'Valor Total',
      value: `R$ ${(data?.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      subtitle: 'soma de todas as parcelas',
      color: '#E85D75',
      bgColor: '#FDE8ED'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      {cards.map((card, index) => (
        <div
          key={index}
          className="rounded-lg p-3 shadow-sm border"
          style={{ backgroundColor: card.bgColor, borderColor: '#E5E7EB' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-xs font-medium" style={{ color: card.color }}>
                {card.title}
              </h4>
              <p className="text-lg font-bold mt-0.5" style={{ color: card.color }}>
                {card.value}
              </p>
              <p className="text-xs text-gray-600 mt-0.5">{card.subtitle}</p>
            </div>
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: card.color }}
            >
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
              </svg>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ParcelasCards;